import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaEnvelope, FaSpinner, FaArrowLeft, FaShieldAlt } from "react-icons/fa";
import axios from "axios";
import { api } from "../utils/apiUtils";
import { API_URL } from "../config/apiConfig";
import { getCorporateToken, getCorporateMember } from "../utils/corporateSession";
import { getFcmToken } from "../firebase/getFcmToken";

const CorporateLogin = () => {
  const navigate = useNavigate();
  const otpInputRef = useRef(null);

  const [step, setStep] = useState("identifier");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [promoImages, setPromoImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Fetch corporate login promotional images
  useEffect(() => {
    const fetchPromoImages = async () => {
      try {
        const response = await axios.get(`${API_URL}/auth-images`, {
          params: { type: 'corporate_login' }
        });
        if (response.data.success && response.data.data.length > 0) {
          setPromoImages(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching corporate login images:", error);
      }
    };
    fetchPromoImages();
  }, []);

  // Auto-rotate images
  useEffect(() => {
    if (promoImages.length > 1) {
      const timer = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % promoImages.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [promoImages]);

  // Redirect if already logged in
  useEffect(() => {
    const corporateToken = getCorporateToken();
    const corporateMember = getCorporateMember();
    if (corporateToken && corporateMember) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // Auto-submit OTP
  useEffect(() => {
    if (step === "otp" && otp.length === 6 && !loading) {
      const timer = setTimeout(() => {
        verifyOtp({ preventDefault: () => {} });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [otp, step, loading]);

  const sendOtp = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/corporate/auth/send-otp", { email });
      if (res?.data?.success) {
        setStep("otp");
        if (res?.data?.data?.debugOtp) {
          setTimeout(() => setOtp(res.data.data.debugOtp), 500);
        }
      } else {
        toast.error(res?.data?.message || "Failed to send OTP");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!otp) {
      toast.error("Please enter OTP");
      return;
    }
    setLoading(true);
    try {
      let fcmToken = null;
      try {
        fcmToken = await getFcmToken();
      } catch (err) {
        console.error("Failed to get FCM token:", err);
      }
      
      const res = await api.post("/corporate/auth/verify-otp", { email, otp, fcmToken });
      if (res?.data?.success) {
        const corporateToken = res?.data?.corporateToken || res?.data?.token;
        if (corporateToken) localStorage.setItem("corporateToken", corporateToken);
        const member = res?.data?.data || {};
        if (member && Object.keys(member).length > 0) {
          localStorage.setItem("corporateMember", JSON.stringify(member));
        }
        sessionStorage.removeItem("corporateBookingMode");
        window.dispatchEvent(new Event("corporateSessionChanged"));
        navigate("/", { replace: true });
      } else {
        toast.error(res?.data?.message || "Invalid OTP");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F2F2] flex items-center justify-center p-2 sm:p-4 py-6 sm:py-12">
      <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl w-full max-w-[1000px] flex flex-col md:flex-row overflow-hidden relative min-h-0 md:min-h-[550px]">
        {/* Left Side - Promo Image */}
        <div className="hidden md:block w-[45%] relative bg-[#F56753] overflow-hidden">
          {promoImages.length > 0 ? (
            <div className="absolute inset-0">
              <img
                src={promoImages[currentImageIndex].imageUrl}
                alt={promoImages[currentImageIndex].title}
                className="w-full h-full object-cover transition-opacity duration-1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-12 left-10 right-10 text-white">
                <h3 className="text-3xl font-black mb-4 leading-tight">
                  {promoImages[currentImageIndex].title}
                </h3>
                <p className="text-sm opacity-90 leading-relaxed font-medium">
                  {promoImages[currentImageIndex].description}
                </p>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <FaSpinner className="animate-spin text-white text-4xl" />
            </div>
          )}
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full md:w-[55%] p-5 pt-16 pb-6 sm:p-12 md:p-16 flex flex-col justify-center relative">
          <button 
            onClick={() => navigate("/")}
            className="absolute top-5 left-5 text-gray-400 hover:text-[#F56753] transition-colors flex items-center gap-1.5 text-xs sm:text-sm font-bold"
          >
            <FaArrowLeft /> BACK TO HOME
          </button>

          <div className="mb-6 md:mb-10 text-center md:text-left">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 mb-2 md:mb-3 tracking-tight">Corporate Login</h1>
            <p className="text-gray-500 font-bold tracking-wide uppercase text-[10px] sm:text-xs">Admin Access • Secured by OTP</p>
          </div>

          {step === "identifier" && (
            <form onSubmit={sendOtp} className="space-y-6 md:space-y-8 animate-fadeIn">
              <div className="group">
                <label className="block text-[10px] md:text-[11px] font-black text-gray-400 uppercase tracking-[2px] mb-3 px-1 group-focus-within:text-[#F56753] transition-colors">
                  Corporate Email
                </label>
                <div className="relative border-b-2 border-gray-100 group-focus-within:border-[#F56753] transition-all duration-300 pb-1.5 md:pb-2">
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-[#F56753] transition-colors">
                    <FaEnvelope size={18} />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-8 py-2 bg-transparent text-lg md:text-xl font-bold text-gray-800 focus:outline-none placeholder:text-gray-200"
                    placeholder="Enter your work email"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !email}
                className={`w-full py-3.5 md:py-5 rounded-full font-black text-base md:text-lg tracking-[2px] shadow-xl transition-all duration-300 active:scale-95 ${
                  loading || !email
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-[#F56753] to-[#d94e3a] text-white hover:shadow-red-200/50 hover:shadow-2xl"
                }`}
              >
                {loading ? <FaSpinner className="animate-spin mx-auto text-2xl" /> : "CONTINUE"}
              </button>

              <div className="relative py-3">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100"></div></div>
                <div className="relative flex justify-center text-[10px]"><span className="px-4 bg-white text-gray-400 font-black tracking-widest uppercase">New Organization?</span></div>
              </div>

              <button
                type="button"
                onClick={() => navigate("/corporate/register")}
                className="w-full py-3 rounded-full border-2 border-gray-100 font-black text-gray-400 text-[10px] sm:text-xs tracking-widest hover:border-[#F56753] hover:text-[#F56753] transition-all duration-300"
              >
                REGISTER CORPORATE ADMIN
              </button>
            </form>
          )}

          {step === "otp" && (
            <form onSubmit={verifyOtp} className="space-y-6 md:space-y-8 animate-fadeIn">
              <div className="bg-red-50/50 p-4 rounded-2xl border border-red-100 flex items-start gap-3">
                <div className="mt-1 text-[#F56753]"><FaShieldAlt size={18} /></div>
                <div>
                  <p className="text-xs font-bold text-red-900 leading-tight">Verification Required</p>
                  <p className="text-[11px] text-red-700 mt-1">OTP has been sent to <span className="font-black underline">{email}</span></p>
                </div>
              </div>
 
              <div className="group">
                <label className="block text-[10px] md:text-[11px] font-black text-gray-400 uppercase tracking-[2px] mb-3 px-1 group-focus-within:text-[#F56753]">
                  Enter 6-Digit OTP
                </label>
                <input
                  ref={otpInputRef}
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="w-full text-center text-2xl sm:text-4xl font-black tracking-[8px] md:tracking-[12px] py-2 md:py-4 border-b-2 border-gray-100 focus:border-[#F56753] focus:outline-none transition-all duration-300 text-gray-800"
                  placeholder="000000"
                  maxLength={6}
                  inputMode="numeric"
                  required
                />
              </div>

              <div className="flex flex-col gap-4">
                <button
                  type="submit"
                  disabled={loading || otp.length < 6}
                  className={`w-full py-3.5 md:py-5 rounded-full font-black text-base md:text-lg tracking-[2px] shadow-xl transition-all duration-300 active:scale-95 ${
                    loading || otp.length < 6
                      ? "bg-gray-100 text-gray-400"
                      : "bg-gradient-to-r from-[#F56753] to-[#d94e3a] text-white"
                  }`}
                >
                  {loading ? <FaSpinner className="animate-spin mx-auto text-2xl" /> : "VERIFY & LOGIN"}
                </button>
                <button
                  type="button"
                  onClick={() => setStep("identifier")}
                  className="text-[10px] md:text-[11px] font-black text-[#F56753] tracking-widest uppercase hover:underline text-center"
                >
                  Change Email Address
                </button>
              </div>
            </form>
          )}

          <div className="mt-8 md:mt-12 text-center">
            <p className="text-[9px] md:text-[10px] text-gray-400 font-bold leading-relaxed uppercase tracking-wider">
              Protected by Enterprise Grade Security • CabZigO © 2024
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorporateLogin;
